/**
 * Landing CMS — thin client for the superadmin editor endpoints.
 *
 * All methods return the inner `data` payload from the standard
 * api_response envelope when successful, or throw the axios error so the
 * caller can show a banner.
 *
 * The endpoints are documented in ovacs_back/api/landing_cms/views.py.
 */

import { apiService } from '../../../services/api'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

export const landingCmsApi = {
  /** GET — editor state: both languages with draft+published + settings. */
  getState: async () => unwrap(await apiService.get('/superadmin/landing-cms/state/')),

  /** PUT — save the draft for one language. payload is the full content object. */
  saveDraft: async (lang, payload) =>
    unwrap(await apiService.put(`/superadmin/landing-cms/draft/${lang}/`, { payload })),

  /** POST — atomic publish of BOTH languages.  Optional `note` for history. */
  publish: async (note = '') =>
    unwrap(await apiService.post('/superadmin/landing-cms/publish/', { note })),

  /** POST — reset both drafts back to published. */
  discard: async () => unwrap(await apiService.post('/superadmin/landing-cms/discard/')),

  /** PUT — update page-wide settings (hero mode, language switch, video URLs). */
  saveSettings: async (patch) =>
    unwrap(await apiService.put('/superadmin/landing-cms/settings/', patch)),

  /** GET — last 50 publishes for the History drawer. */
  listRevisions: async () => unwrap(await apiService.get('/superadmin/landing-cms/revisions/')),

  /** POST — copy a past revision back into the draft. */
  restoreRevision: async (id) =>
    unwrap(await apiService.post(`/superadmin/landing-cms/revisions/${id}/restore/`)),

  /**
   * POST — upload a file (image/video/logo).  Returns
   *   { id, url, kind, original_filename, mime_type, size_bytes, width, height, ... }
   * The `url` is what should go into the content payload.
   *
   * We build the FormData manually (rather than using apiService.uploadFiles)
   * because the backend reads `request.FILES.get('file')` (singular) while
   * apiService.uploadFiles uses field name `files` (plural).
   */
  uploadMedia: async (file, kind = '', onProgress) => {
    const fd = new FormData()
    fd.append('file', file)
    if (kind) fd.append('kind', kind)
    const config = {}
    if (typeof onProgress === 'function') {
      config.onUploadProgress = (e) => {
        if (e.total) onProgress(Math.round((e.loaded * 100) / e.total))
      }
    }
    return unwrap(await apiService.post('/superadmin/landing-cms/media/upload/', fd, config))
  },

  /** GET — list uploaded media for the picker library. */
  listMedia: async (kind = '') => {
    const path = kind
      ? `/superadmin/landing-cms/media/?kind=${encodeURIComponent(kind)}`
      : '/superadmin/landing-cms/media/'
    return unwrap(await apiService.get(path))
  },

  /** DELETE — remove an uploaded media row + file from disk. */
  deleteMedia: async (id) =>
    unwrap(await apiService.delete(`/superadmin/landing-cms/media/${id}/`)),

  /**
   * POST — replace the draft for one language with an uploaded JSON payload.
   * Caller has already parsed + validated the JSON shape.
   */
  importPayload: async (lang, payload) =>
    unwrap(await apiService.post(`/superadmin/landing-cms/import/${lang}/`, { payload })),
}

export default landingCmsApi
